<template>
  <div class="actions-group">
    <div
      class="action-item"
      :class="{ disabled: !isServiceEnabled('mocap') }"
      :title="getServiceTitle('mocap')"
      @click="handleServiceClick('mocap', '/upload')"
    >
      <div class="description">
        <p class="header-2">Ava Video</p>
        <p class="caption">
          {{ $t('animation.home.videoDescription') }}
        </p>
        <SecondBtn class="action-btn" :disabled="!isServiceEnabled('mocap')">{{ $t('animation.home.start') }}</SecondBtn>
      </div>
      <img src="@/assets/video.png" :alt="$t('app.logoAlt')" />
    </div>

    <div
      class="action-item prompt-item"
      :class="{ disabled: !isServiceEnabled('prompt') }"
      :title="getServiceTitle('prompt')"
      @click="handleServiceClick('prompt', '/prompt')"
    >
      <div class="description">
        <p class="header-2">Ava Prompt</p>
        <p class="caption">{{ $t('animation.home.promptDescription') }}</p>
        <SecondBtn class="action-btn" :disabled="!isServiceEnabled('prompt')">{{ $t('animation.home.start') }}</SecondBtn>
      </div>
      <img src="@/assets/prompt.png" :alt="$t('app.logoAlt')" />
    </div>
  </div>

  <ProjectList
    :name="$t('layout.navigation.animations')"
    :projectItems="projectItems"
    :loading="isInitialLoad ? isLoading : false"
    :error="isError"
    :emptyCtaText="$t('animation.home.createFirst')"
    emptyCtaRoute="/upload"
    @retry="fetchProjectItems"
  />
  <ProjectList :name="$t('layout.navigation.tutorials')" :locked="true" />
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import SecondBtn from "@/components/BaseComponents/SecondBtn.vue";
import ProjectList from "@/components/ProjectList.vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { animationAPI } from "@/services/api";
import { useAvailability } from "@/composables/useAvailability";
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const router = useRouter();
const projectItems = ref([]);
const isLoading = ref(true);
const isError = ref(false);
const pollingInterval = ref(null);
const isInitialLoad = ref(true);
const isActive = ref(true);
const { getServiceEntry } = useAvailability();
const { t } = useI18n();
const serviceEntries = computed(() => ({
  mocap: getServiceEntry("mocap"),
  prompt: getServiceEntry("prompt"),
}));

watch(
  () => route.path,
  (newPath) => {
    isActive.value = newPath === "/app/home";
    if (!isActive.value) {
      stopPolling();
    } else {
      // If we're navigating to the home page, check if we need to start polling
      if (
        projectItems.value.some(
          (item) =>
            item.animation_status === "Started" || item.animation_status === "None"
        )
      ) {
        startPolling();
      }
    }
  }
);

function isServiceEnabled(serviceKey) {
  return serviceEntries.value[serviceKey]?.enabled !== false;
}

function getServiceTitle(serviceKey) {
  const service = serviceEntries.value[serviceKey];
  return service?.enabled === false
    ? service.message || t('animation.home.unavailable')
    : "";
}

async function handleServiceClick(serviceKey, routePath) {
  await store.dispatch("fetchAvailability");

  if (!isServiceEnabled(serviceKey)) {
    return;
  }

  stopPolling();
  router.push(routePath);
}

async function fetchProjectItems() {
  if (isInitialLoad.value) {
    isLoading.value = true;
  }
  isError.value = false;

  try {
    const sessionId = store.getters.getSessionId;
    const data = await animationAPI.getAnimationList(sessionId, router);
    projectItems.value = data;

    if (
      data.some(
        (item) => item.animation_status === "Started" || item.animation_status === "None"
      )
    ) {
      startPolling();
    } else {
      stopPolling();
    }
  } catch (error) {
    console.error("Error fetching project items:", error);
    projectItems.value = [];
    isError.value = true;
  } finally {
    if (isInitialLoad.value) {
      isLoading.value = false;
      isInitialLoad.value = false;
    }
  }
}

function startPolling() {
  stopPolling();

  if (!isActive.value) return; // Don't start polling if not active

  pollingInterval.value = setInterval(() => {
    if (isActive.value) {
      const currentLoading = isLoading.value;
      isLoading.value = false;
      fetchProjectItems().finally(() => {
        isLoading.value = currentLoading;
      });
    } else {
      stopPolling();
    }
  }, 5000);
}

function stopPolling() {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
}

onMounted(() => {
  isActive.value = route.path === "/app/home";
  fetchProjectItems();
});

onUnmounted(() => {
  isActive.value = false;
  stopPolling();
});
</script>

<style scoped>
.actions-group {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
}

.action-item {
  flex: 1 1 calc(50% - var(--spacing-lg));
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  background-color: var(--color-white-10);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg) var(--spacing-xl);
  box-sizing: border-box;
  min-width: 250px;
  cursor: pointer;
  position: relative;
}

.action-item:hover:not(.disabled) {
  background-color: var(--color-white-20);
  transition-duration: var(--transition-instant);
}

.action-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.action-item img {
  height: 100%;
  width: auto;
  max-width: 42%;
}

.description {
  flex: 1;
  margin-right: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.description .caption {
  color: var(--color-white-40);
}

.action-btn {
  width: auto;
  align-self: flex-start;
}

.prompt-item .hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--overlay-dark-60);
  color: var(--color-white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal);
  border-radius: var(--border-radius-md);
}

.prompt-item:hover .hover-overlay {
  opacity: 1;
}

.overlay-icon {
  margin-bottom: var(--spacing-sm-lg);
}

@media (max-width: 768px) {
  .actions-group {
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .action-item {
    flex: 1 1 100%;
    align-items: center;
  }

  .action-item img {
    max-width: 100%;
    width: 100px;
    height: auto;
    margin-top: var(--spacing-sm-lg);
  }

  .description {
    margin-right: 0;
  }

  .action-btn {
    width: auto;
    align-self: flex-start;
  }
}
</style>
